/**
 * Logica pura del modulo ENISA nel portale: visibilità dei campi condizionali, precompilazione
 * senza sovrascrivere la bozza, campi obbligatori mancanti, date UTC. Le stesse regole sono
 * applicate (e fanno fede) lato srp-service.
 */
import type {
  EnisaFieldDef,
  EnisaFormSchema,
  EnisaFormValues,
  EnisaGroupDef,
  EnisaGroupRow,
  EnisaScalar,
} from './types.js';

export const isBlank = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

export const topFields = (schema: EnisaFormSchema): EnisaFieldDef[] =>
  schema.nodes.filter((n): n is EnisaFieldDef => n.kind === 'field');

export const groups = (schema: EnisaFormSchema): EnisaGroupDef[] =>
  schema.nodes.filter((n): n is EnisaGroupDef => n.kind === 'group');

/** Campi visibili di un livello: i condizionali seguono il padre quando la sua opzione è scelta. */
export function visibleFields(
  fields: EnisaFieldDef[],
  level: Record<string, unknown>,
): Array<{ field: EnisaFieldDef; depth: number }> {
  const out: Array<{ field: EnisaFieldDef; depth: number }> = [];
  const walk = (list: EnisaFieldDef[], depth: number) => {
    for (const field of list) {
      out.push({ field, depth });
      const selected = level[field.id];
      const opt =
        typeof selected === 'string' ? field.options?.find((o) => o.value === selected) : undefined;
      if (opt?.fields?.length) walk(opt.fields, depth + 1);
    }
  };
  walk(fields, 0);
  return out;
}

/** Imposta un valore in un livello e scarta i condizionali non più visibili. */
export function setInLevel<T extends Record<string, unknown>>(
  fields: EnisaFieldDef[],
  level: T,
  id: string,
  value: EnisaScalar,
): T {
  const next: Record<string, unknown> = { ...level, [id]: value };
  const visibili = new Set(visibleFields(fields, next).map((v) => v.field.id));
  for (const key of Object.keys(next)) {
    if (!visibili.has(key) && isFieldOfLevel(fields, key)) delete next[key];
  }
  return next as T;
}

function isFieldOfLevel(fields: EnisaFieldDef[], id: string): boolean {
  return fields.some(
    (f) => f.id === id || (f.options ?? []).some((o) => isFieldOfLevel(o.fields ?? [], id)),
  );
}

/**
 * Valori iniziali del modulo: la bozza salvata vince sempre; la precompilazione riempie solo i
 * campi (o gruppi) che la bozza non ha mai avuto. Ritorna anche i percorsi precompilati (per
 * segnalarli finché l'utente non li tocca).
 */
export function initialValues(
  draft: EnisaFormValues | undefined,
  prefill: EnisaFormValues,
): { values: EnisaFormValues; prefilled: Set<string> } {
  const values: EnisaFormValues = { ...(draft ?? {}) };
  const prefilled = new Set<string>();
  for (const [key, value] of Object.entries(prefill)) {
    if (key in values) continue;
    values[key] = value;
    if (Array.isArray(value) && value.some((v) => typeof v === 'object')) {
      (value as EnisaGroupRow[]).forEach((row, i) => {
        for (const f of Object.keys(row)) prefilled.add(`${key}[${i}].${f}`);
      });
    } else {
      prefilled.add(key);
    }
  }
  return { values, prefilled };
}

/** Righe di un gruppo, almeno `minItems` (le righe minime sono vuote). */
export function groupRows(group: EnisaGroupDef, values: EnisaFormValues): EnisaGroupRow[] {
  const rows = (values[group.id] as EnisaGroupRow[] | undefined) ?? [];
  if (rows.length >= group.minItems) return rows;
  return [...rows, ...Array.from({ length: group.minItems - rows.length }, () => ({}))];
}

/** Percorsi obbligatori visibili ancora vuoti (stesso ordine del server). */
export function missingRequired(schema: EnisaFormSchema, values: EnisaFormValues): string[] {
  const missing: string[] = [];
  const check = (fields: EnisaFieldDef[], level: Record<string, unknown>, prefix: string) => {
    for (const { field } of visibleFields(fields, level)) {
      if (field.required && isBlank(level[field.id])) missing.push(`${prefix}${field.id}`);
    }
  };
  check(topFields(schema), values, '');
  for (const g of groups(schema)) {
    groupRows(g, values).forEach((row, i) => check(g.fields, row, `${g.id}[${i}].`));
  }
  return missing;
}

/** Valore `YYYY-MM-DDTHH:MMZ` → parti; vuoto se non valido. */
export function dateParts(value: unknown): { day: string; hour: string; minute: string } {
  const m = typeof value === 'string' ? /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})Z$/.exec(value) : null;
  return m ? { day: m[1]!, hour: m[2]!, minute: m[3]! } : { day: '', hour: '', minute: '' };
}

/** Parti → valore `YYYY-MM-DDTHH:MMZ` (ora/minuto 00 se non scelti); '' senza giorno. */
export function composeDate(day: string, hour: string, minute: string): string {
  if (!day) return '';
  return `${day}T${hour || '00'}:${minute || '00'}Z`;
}
